import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { haveQueryParamsChanged } from "utils";
import {
  NO_NEW_FILTER_MESSAGE,
  SEARCH_DEBOUNCE_TIMER,
  ZERO,
} from "utils/constants";

import { customWarningToast } from "ui/custom-toast";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import { IBusinessDetailsAccountsTableHeaderCTAProps } from "./types";

const filterOptions: TFilterOption[] = [
  {
    type: "checkbox",
    title: "Account Type",
    key: "accountType",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Expense",
        value: "expense",
      },
      {
        label: "Collection",
        value: "collection",
      },
    ],
  },
  {
    type: "checkbox",
    title: "Active Status",
    key: "activeStatus",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Active",
        value: "active",
      },
      {
        label: "Inactive",
        value: "inactive",
      },
      {
        label: "Frozen",
        value: "frozen",
      },
    ],
  },
  {
    type: "date-range",
    title: "Custom Date Range",
    key: "dateRange",
  },
];
const ALL_ACCOUNT_TYPE_LIST = ["all", "expense", "collection"];
const ALL_ACTIVE_STATUS_LIST = ["all", "active", "inactive", "frozen"];

export const BusinessDetailsAccountsTableHeaderCTA = memo(
  (props: IBusinessDetailsAccountsTableHeaderCTAProps) => {
    const {
      handleGetBusinessAccounts,
      isLoading,
      isRefreshing,
      setIsRefreshing,
    } = props;
    const { businessId } = useParams();
    const location = useLocation();
    const updateQueryParams = useUpdateQueryParams();
    const [searchParams] = useSearchParams();
    const walletType = searchParams.get("walletType");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const accountStatus = searchParams.get("accountStatus");
    const status = searchParams.get("status");
    const { isFilterError, setIsFilterError, filterRetryCountParams } =
      useFilterQueryParams({
        apiFilterRoute: "/control/business/compliance/accounts",
      });
    const [searchQuery, setSearchQuery] = useState("");
    const filterRef = useRef<IFilterRef | null>(null);
    const [isFiltering, setIsFiltering] = useState(false);
    const [filterValues, setFilterValues] = useState<TFilterValues>({
      accountType: walletType ? walletType.split(",") : [],
      activeStatus:
        status || accountStatus
          ? [...(status?.split(",") || []), accountStatus || ""]
          : [],
      dateRange: {
        startDate,
        endDate,
      },
    });
    const isFilterDisabled = !(
      (filterValues?.accountType as string[])?.length > ZERO ||
      (filterValues?.dateRange as IDateRangeValues)?.startDate ||
      (filterValues?.activeStatus as string[])?.length > ZERO
    );

    const handleResetFilter = () => {
      setFilterValues({
        accountType: [],
        activeStatus: [],
        dateRange: {
          startDate: null,
          endDate: null,
        },
      });
    };

    const onBusinessAccountSearch = useCallback(
      debounce(async (value: string) => {
        if (value.trim() === "") {
          await handleGetBusinessAccounts({
            businessId,
          });
          return;
        }

        await handleGetBusinessAccounts({
          businessId: businessId,
          search: value,
          ...Object.fromEntries(new URLSearchParams(location.search)),
        });
      }, SEARCH_DEBOUNCE_TIMER),
      [location]
    );

    const handleSearchQueryChange = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = evt.target;
      setSearchQuery(value);
      onBusinessAccountSearch(value);
    };

    const handleFilterValueChange = (key: string, value: TFilterValue) => {
      let newValue = value;
      if (key === "activeStatus") {
        const activeStatus = value as string[];
        const currentAccountStatus = filterValues?.activeStatus as string[];
        if (
          activeStatus.includes("all") &&
          currentAccountStatus.length < ALL_ACTIVE_STATUS_LIST.length
        ) {
          newValue = ALL_ACTIVE_STATUS_LIST;
        } else {
          newValue = activeStatus.filter((acctStatus) => acctStatus !== "all");
        }
      } else if (key === "accountType") {
        const accountType = value as string[];
        const currentAccountType = filterValues?.accountType as string[];
        if (
          accountType.includes("all") &&
          currentAccountType.length < ALL_ACCOUNT_TYPE_LIST.length
        ) {
          newValue = ALL_ACCOUNT_TYPE_LIST;
        } else {
          newValue = accountType.filter((acctType) => acctType !== "all");
        }
      }
      setFilterValues((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    };

    const handleApplyFilter = async () => {
      const newQueryParams: Record<string, string | null> = {};
      const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
      const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
      const status = filterValues.activeStatus;
      const walletType = filterValues.accountType;

      if (startDate) {
        newQueryParams["startDate"] = startDate;
      } else {
        newQueryParams["startDate"] = null;
      }

      if (endDate) {
        newQueryParams["endDate"] = endDate;
      } else {
        newQueryParams["endDate"] = null;
      }

      if (Array.isArray(status) && status.length > ZERO) {
        const containsActive = status.includes("active");
        const containsInactive = status.includes("inactive");
        const containsFrozen = status.includes("frozen");
        if (containsActive || containsInactive) {
          newQueryParams["status"] = status.join(",");
        } else {
          newQueryParams["status"] = null;
        }

        if (containsFrozen) {
          newQueryParams["accountStatus"] = "frozen";
        } else {
          newQueryParams["accountStatus"] = null;
        }
      } else {
        newQueryParams.status = null;
        newQueryParams.accountStatus = null;
      }

      if (Array.isArray(walletType) && walletType.length > ZERO) {
        newQueryParams["walletType"] = walletType.join(",");
      } else {
        newQueryParams.walletType = null;
      }

      setIsFilterError(false);
      updateQueryParams({
        ...newQueryParams,
        ...filterRetryCountParams,
      });
      if (
        haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: newQueryParams,
        }) ||
        isFilterError
      ) {
        setIsFiltering(true);
      } else {
        customWarningToast({
          message: NO_NEW_FILTER_MESSAGE,
        });
      }
    };

    const handleRefreshTable = async () => {
      setIsRefreshing(true);
      if (
        !haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: {},
        })
      ) {
        await handleGetBusinessAccounts({ isRefetching: true });
        setIsRefreshing(false);
      } else {
        setSearchQuery("");
        updateQueryParams({});
      }
    };

    useEffect(() => {
      if (!isLoading && filterRef?.current?.isFilterOpen) {
        setIsFiltering(false);
        filterRef?.current?.handleToggleFilter();
      }
    }, [isLoading, filterRef]);

    useEffect(() => {
      if (!isLoading && isRefreshing) {
        handleResetFilter();
        setIsRefreshing(false);
      }
    }, [isLoading]);

    return (
      <Box className={`flex px-3 items-center justify-between h-[85px]`}>
        <Box className="flex items-center gap-4">
          <TableSearchInput
            searchQuery={searchQuery}
            handleSearchQueryChange={handleSearchQueryChange}
          />
          <Filter
            options={filterOptions}
            values={filterValues}
            onChange={handleFilterValueChange}
            onApplyFilter={handleApplyFilter}
            filterBtnCss={tableHeaderCtaCss}
            isLoading={isFiltering}
            ref={filterRef}
            isApplyBtnDisabled={isFilterDisabled}
            handleResetFilter={handleResetFilter}
          />
          <Button
            css={tableHeaderCtaCss}
            onClick={!isRefreshing ? handleRefreshTable : undefined}
            disabled={isRefreshing}
          >
            <RefreshIcon
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            <p className="text-sm text-[#374151] font-semibold">Refresh</p>
          </Button>
        </Box>
      </Box>
    );
  }
);
